// Tools → HTML Hosting — MULTI-FILE BUNDLES: the pure rules.
//
// A bundle is a .zip of an HTML page plus its supporting folders (images,
// video, CSS, JS, fonts). Everything that decides WHAT IS ACCEPTED lives here
// so it is unit-testable without a zip library, GCS or Firestore, and so the
// upload form and the server cannot disagree.
//
// ⚠️⚠️ A ZIP IS UNTRUSTED INPUT FROM A STAFF MEMBER'S MACHINE, AND THE ENTRY
// NAMES ARE ATTACKER-CONTROLLED. Every rule below exists because the matching
// failure is a classic: zip-slip (an entry named `../../x` escaping its
// directory), symlinks and devices, zip bombs (a few KB that inflate to
// gigabytes), and a file type that turns the host into an XSS or malware
// host. Content-Type is NEVER taken from the archive; it comes from OUR
// extension map, and anything off the allowlist is rejected, not skipped.

export const BUNDLE_LIMITS = {
  /** The compressed upload. The browser sends it direct to GCS, never via Cloud Run. */
  maxUploadBytes: 200 * 1024 * 1024,
  /** Total uncompressed size across every file. */
  maxUncompressedBytes: 300 * 1024 * 1024,
  maxFiles: 2000,
  /**
   * Per-entry compression ratio (uncompressed / compressed), applied only to
   * entries at or above `ratioFloorBytes` — a tiny all-zero file is a huge
   * ratio and harmless, while a 1 MiB entry from a few bytes is the bomb shape.
   * Already-compressed media (jpg/mp4) sits near 1, and text rarely passes 20.
   */
  maxRatio: 200,
  ratioFloorBytes: 1024 * 1024,
  maxPathLength: 240,
  maxPathSegments: 16,
} as const;

/** Extension → the Content-Type WE serve it as. The allowlist, in one table. */
export const BUNDLE_CONTENT_TYPES: Readonly<Record<string, string>> = {
  html: 'text/html; charset=utf-8',
  htm: 'text/html; charset=utf-8',
  css: 'text/css; charset=utf-8',
  js: 'text/javascript; charset=utf-8',
  mjs: 'text/javascript; charset=utf-8',
  json: 'application/json; charset=utf-8',
  map: 'application/json; charset=utf-8',
  txt: 'text/plain; charset=utf-8',
  csv: 'text/csv; charset=utf-8',
  svg: 'image/svg+xml',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  ico: 'image/x-icon',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  otf: 'font/otf',
  pdf: 'application/pdf',
};

export const BUNDLE_EXTENSIONS: readonly string[] = Object.keys(BUNDLE_CONTENT_TYPES);

/** Names macOS / Windows add that nobody uploaded on purpose. Dropped silently. */
const JUNK_BASENAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

export function isJunkPath(path: string): boolean {
  const segments = path.split('/');
  if (segments[0] === '__MACOSX') return true;
  const base = (segments[segments.length - 1] ?? '').toLowerCase();
  return JUNK_BASENAMES.has(base);
}

export function extensionOf(path: string): string {
  const base = path.split('/').pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot + 1).toLowerCase();
}

/** `null` = not on the allowlist. */
export function contentTypeForPath(path: string): string | null {
  return BUNDLE_CONTENT_TYPES[extensionOf(path)] ?? null;
}

export function isHtmlPath(path: string): boolean {
  const ext = extensionOf(path);
  return ext === 'html' || ext === 'htm';
}

// ---------- path normalisation ----------

export type PathResult = { ok: true; path: string } | { ok: false; reason: string };

/**
 * A zip entry name → a safe relative path inside the bundle, or the reason it
 * was refused.
 *
 * ⚠️ ZIP-SLIP LIVES HERE. Refused outright, never "cleaned": absolute paths
 * (leading `/`), drive letters, any `..` segment, backslashes (a Windows-style
 * separator is how `..\..\x` survives a forward-slash check), NUL and other
 * control characters, and empty segments. Cleaning a hostile name into a
 * different valid one would silently ship something nobody uploaded.
 *
 * ⚠️ `?`, `#` and `%` are refused too — not for safety but because a file with
 * one in its name cannot be addressed by a relative URL (`a#b.png` is read as
 * `a` with a fragment), so the page that references it would break with no
 * explanation. The message tells staff to rename it.
 */
export function normaliseEntryPath(raw: string): PathResult {
  if (raw.length === 0) return { ok: false, reason: 'has an empty name' };
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return { ok: false, reason: 'has a control character in its name' };
  if (raw.includes('\\')) return { ok: false, reason: 'uses a backslash in its path (zip it again with forward slashes)' };
  if (raw.startsWith('/')) return { ok: false, reason: 'has an absolute path' };
  if (/^[A-Za-z]:/.test(raw)) return { ok: false, reason: 'has a drive letter in its path' };
  if (/[?#%]/.test(raw)) return { ok: false, reason: 'has a "?", "#" or "%" in its name, which cannot be linked to — rename it' };
  const segments: string[] = [];
  for (const seg of raw.split('/')) {
    if (seg === '..') return { ok: false, reason: 'tries to climb out of the folder ("..")' };
    if (seg === '.') continue;
    if (seg === '') return { ok: false, reason: 'has an empty path segment' };
    segments.push(seg);
  }
  if (segments.length === 0) return { ok: false, reason: 'has an empty name' };
  const path = segments.join('/');
  if (path.length > BUNDLE_LIMITS.maxPathLength) return { ok: false, reason: `has a path longer than ${BUNDLE_LIMITS.maxPathLength} characters` };
  if (segments.length > BUNDLE_LIMITS.maxPathSegments) return { ok: false, reason: `is nested more than ${BUNDLE_LIMITS.maxPathSegments} folders deep` };
  return { ok: true, path };
}

/**
 * The SERVING-side twin: a URL path (already percent-decoded by the framework)
 * → a bundle-relative path, or null. Stricter than the extraction side only in
 * that it also refuses a trailing slash (no directory listings, ever).
 */
export function decodeServePath(decoded: string): string | null {
  if (decoded.endsWith('/')) return null;
  const r = normaliseEntryPath(decoded);
  if (!r.ok) return null;
  // `./` would have been dropped by the normaliser; a request whose normal
  // form differs from what was asked for is refused rather than rewritten.
  return r.path === decoded ? r.path : null;
}

// ---------- entry classification ----------

/** What `yauzl` tells us about one entry, reduced to the fields the rules read. */
export type ZipEntryInfo = {
  fileName: string;
  compressedSize: number;
  uncompressedSize: number;
  /** General-purpose bit flags; bit 0 = encrypted. */
  flags: number;
  /** "version made by" — the high byte is the host system (3 = Unix). */
  versionMadeBy: number;
  externalFileAttributes: number;
};

export type EntryKind = 'file' | 'directory' | 'unsupported';

const S_IFMT = 0o170000;
const S_IFREG = 0o100000;
const S_IFDIR = 0o040000;

/**
 * File, directory, or something we refuse (symlink, device, fifo, socket).
 * ⚠️ A symlink inside a served bundle is a path to read ANOTHER file's bytes
 * out of the extraction; extraction never creates links, so the only safe
 * answer is to refuse the archive that contains one.
 */
export function entryKind(e: Pick<ZipEntryInfo, 'fileName' | 'versionMadeBy' | 'externalFileAttributes'>): EntryKind {
  const host = (e.versionMadeBy >>> 8) & 0xff;
  if (host === 3) {
    const mode = (e.externalFileAttributes >>> 16) & 0xffff;
    const type = mode & S_IFMT;
    if (type === S_IFDIR) return 'directory';
    if (type === S_IFREG || type === 0) return e.fileName.endsWith('/') ? 'directory' : 'file';
    return 'unsupported';
  }
  return e.fileName.endsWith('/') ? 'directory' : 'file';
}

export type BundleFile = { index: number; sourceName: string; path: string; contentType: string; uncompressedSize: number };

export type BundleProblem = { file: string; reason: string };

export type BundlePlan =
  | {
      ok: true;
      files: BundleFile[];
      totalBytes: number;
      /** The top-level folder that was removed, or null. */
      strippedRoot: string | null;
      /** The page to open, or null when staff must choose among `candidates`. */
      entryPath: string | null;
      candidates: string[];
    }
  | { ok: false; problems: BundleProblem[]; message: string };

const MAX_PROBLEMS = 20;

function mb(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * Decide whether a set of zip entries is an acceptable bundle, and what it
 * looks like once extracted. PURE — it reads headers only and touches no bytes.
 *
 * ⚠️ ONE BAD ENTRY REFUSES THE WHOLE ARCHIVE, naming up to 20 of them. A
 * partial bundle (the page without its stylesheet) would "work" and look
 * broken, which is worse than a clear refusal.
 */
export function planBundle(entries: readonly ZipEntryInfo[], preferredEntry?: string | null): BundlePlan {
  if (entries.length > BUNDLE_LIMITS.maxFiles * 3) {
    // Raw count guard before any per-entry work: junk and directories count
    // here, so the ceiling is looser than the file cap.
    return fail([], `That zip has ${entries.length} entries — the limit is ${BUNDLE_LIMITS.maxFiles} files.`);
  }
  const problems: BundleProblem[] = [];
  const note = (file: string, reason: string) => {
    if (problems.length < MAX_PROBLEMS) problems.push({ file, reason });
  };

  type Candidate = { index: number; sourceName: string; path: string; uncompressedSize: number; compressedSize: number };
  const kept: Candidate[] = [];
  const seen = new Set<string>();

  entries.forEach((e, index) => {
    const kind = entryKind(e);
    if (kind === 'directory') return;
    const norm = normaliseEntryPath(e.fileName);
    if (!norm.ok) {
      // A junk name that is also malformed is still junk — but a malformed
      // name is refused FIRST, so a hostile `__MACOSX/../x` cannot hide as junk.
      note(e.fileName, norm.reason);
      return;
    }
    if (kind === 'unsupported') {
      note(e.fileName, 'is a link or special file, which cannot be hosted');
      return;
    }
    if (isJunkPath(norm.path)) return;
    if ((e.flags & 0x1) !== 0) {
      note(e.fileName, 'is password-protected inside the zip');
      return;
    }
    if (contentTypeForPath(norm.path) === null) {
      const ext = extensionOf(norm.path);
      note(e.fileName, ext ? `is a .${ext} file, which is not an allowed type` : 'has no file extension, so its type is not allowed');
      return;
    }
    if (e.uncompressedSize >= BUNDLE_LIMITS.ratioFloorBytes) {
      const ratio = e.compressedSize <= 0 ? Infinity : e.uncompressedSize / e.compressedSize;
      if (ratio > BUNDLE_LIMITS.maxRatio) {
        note(e.fileName, 'is compressed far beyond a normal file and looks like a zip bomb');
        return;
      }
    }
    if (seen.has(norm.path)) {
      note(e.fileName, 'appears twice in the zip');
      return;
    }
    seen.add(norm.path);
    kept.push({ index, sourceName: e.fileName, path: norm.path, uncompressedSize: e.uncompressedSize, compressedSize: e.compressedSize });
  });

  if (problems.length > 0) {
    return fail(problems, `Couldn't use this zip: ${problems.length >= MAX_PROBLEMS ? 'at least ' : ''}${problems.length} problem${problems.length === 1 ? '' : 's'}. ${problems[0]!.file} ${problems[0]!.reason}.`);
  }
  if (kept.length === 0) return fail([], 'That zip has no files in it that can be hosted.');
  if (kept.length > BUNDLE_LIMITS.maxFiles) {
    return fail([], `That zip has ${kept.length} files — the limit is ${BUNDLE_LIMITS.maxFiles}.`);
  }
  const totalBytes = kept.reduce((n, f) => n + f.uncompressedSize, 0);
  if (totalBytes > BUNDLE_LIMITS.maxUncompressedBytes) {
    return fail([], `That zip unpacks to ${mb(totalBytes)} — the limit is ${mb(BUNDLE_LIMITS.maxUncompressedBytes)}.`);
  }

  // Root stripping: every file under ONE top-level folder → remove it.
  let strippedRoot: string | null = null;
  const firsts = new Set(kept.map((f) => f.path.split('/')[0]!));
  if (firsts.size === 1 && kept.every((f) => f.path.includes('/'))) {
    strippedRoot = [...firsts][0]!;
  }
  const files: BundleFile[] = kept.map((f) => {
    const path = strippedRoot ? f.path.slice(strippedRoot.length + 1) : f.path;
    return { index: f.index, sourceName: f.sourceName, path, contentType: contentTypeForPath(path)!, uncompressedSize: f.uncompressedSize };
  });

  // Entry page.
  const rootHtml = files.map((f) => f.path).filter((p) => !p.includes('/') && isHtmlPath(p));
  let entryPath: string | null = null;
  if (preferredEntry) {
    if (!files.some((f) => f.path === preferredEntry && isHtmlPath(f.path))) {
      return fail([], `"${preferredEntry}" is not an HTML page in this zip.`);
    }
    entryPath = preferredEntry;
  } else if (files.some((f) => f.path === 'index.html')) {
    entryPath = 'index.html';
  } else if (files.some((f) => f.path === 'index.htm')) {
    entryPath = 'index.htm';
  } else if (rootHtml.length === 1) {
    entryPath = rootHtml[0]!;
  } else if (rootHtml.length === 0) {
    return fail([], 'There is no HTML page at the top level of that zip, so there is nothing to open.');
  }
  return { ok: true, files, totalBytes, strippedRoot, entryPath, candidates: entryPath ? [] : rootHtml.sort() };

  function fail(p: BundleProblem[], message: string): BundlePlan {
    return { ok: false, problems: p, message };
  }
}

// ---------- uploaded-archive checks that need only a size ----------

export function validateBundleUploadSize(sizeBytes: number): { ok: true } | { ok: false; message: string } {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return { ok: false, message: 'That file is empty.' };
  if (sizeBytes > BUNDLE_LIMITS.maxUploadBytes) {
    return { ok: false, message: `That zip is ${mb(sizeBytes)} — the limit is ${mb(BUNDLE_LIMITS.maxUploadBytes)}.` };
  }
  return { ok: true };
}

export function isZipFilename(name: string): boolean {
  return name.trim().toLowerCase().endsWith('.zip');
}

// ---------- HTTP Range ----------

export type RangeResult =
  | { kind: 'none' }
  | { kind: 'range'; start: number; end: number }
  | { kind: 'unsatisfiable' };

/**
 * A single `bytes=` range. Anything we do not handle (multi-range, a unit other
 * than bytes, garbage) is `none`: RFC 7233 lets a server ignore Range and send
 * the whole body, which is always a correct answer.
 */
export function parseRange(header: string | undefined, size: number): RangeResult {
  if (!header) return { kind: 'none' };
  const m = /^bytes=(\d*)-(\d*)$/.exec(header.trim());
  if (!m) return { kind: 'none' };
  const [, a, b] = m;
  if (a === '' && b === '') return { kind: 'none' };
  if (size <= 0) return { kind: 'unsatisfiable' };
  if (a === '') {
    const suffix = Number(b);
    if (suffix <= 0) return { kind: 'unsatisfiable' };
    return { kind: 'range', start: Math.max(0, size - suffix), end: size - 1 };
  }
  const start = Number(a);
  if (start >= size) return { kind: 'unsatisfiable' };
  const end = b === '' ? size - 1 : Math.min(Number(b), size - 1);
  if (end < start) return { kind: 'none' };
  return { kind: 'range', start, end };
}

// ---------- the bundle URL ----------

/** Path of the entry page under a grant — the Location the 302 points at. */
export function bundleEntryUrl(basePath: string, grant: string, entryPath: string): string {
  const encoded = entryPath.split('/').map(encodeURIComponent).join('/');
  return `${basePath}/b/${grant}/${encoded}`;
}
